import {
  Directive,
  ElementRef,
  inject,
  input,
  Renderer2,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

@Directive({
  selector: '[appHighlight]',
  standalone: true
})
export class HighlightDirective implements AfterViewInit, OnChanges {
  private el:ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);

  searchText = input.required<string>();
  private originalHTML: string | null = null;
  private initialized = false;

  private escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // MutationObserver removed: baseline is managed internally without observers

  private storeOriginalHTML() {
    this.originalHTML = this.el.nativeElement.innerHTML;
  }

  private highlight() {
    const host = this.el.nativeElement;
    const searchText = this.searchText().trim();

    // Don't do anything before initialization
    if (!this.initialized) return;

    const hasHighlights = !!host.querySelector('span[style*="color: red"]');

    // Early return if no search text: restore if needed, then exit
    if (searchText.length === 0) {
      if (hasHighlights && this.originalHTML !== null) {
        host.innerHTML = this.originalHTML;
      }
      return;
    }

    // Ensure we have a clean baseline to work from
    if (this.originalHTML === null || (!hasHighlights && this.originalHTML !== host.innerHTML)) {
      // Capture current DOM as original only if it doesn't contain highlights
      this.originalHTML = host.innerHTML;
    }
  if (this.originalHTML === null) return;

    // Fast no-match short-circuit: if original text doesn't contain query (case-insensitive)
    const tempCheck = document.createElement('div');
    tempCheck.innerHTML = this.originalHTML;
    if (tempCheck.textContent === null) return;
    if (!(tempCheck.textContent).toLowerCase().includes(searchText.toLowerCase())) {
      // No matches: if currently highlighted, restore; otherwise no-op
      if (hasHighlights && this.originalHTML !== null) {
        host.innerHTML = this.originalHTML;
      }
      return;
    }

    // Build a working copy to compute highlights without touching live DOM
    const working = document.createElement('div');
    working.innerHTML = this.originalHTML;

    const escaped = this.escapeRegExp(searchText);
    const regex = new RegExp(`(${escaped})`, 'gi');

    const walkTextNodes = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (!text.trim()) return;

        const parts = text.split(regex);
        if (parts.length <= 1) return;

        const fragment = document.createDocumentFragment();
        parts.forEach((part: string, i: number) => {
          if (i % 2 === 1 && part.toLowerCase() === searchText.toLowerCase()) {
            const span = this.renderer.createElement('span');
            this.renderer.setAttribute(span, 'style', 'color: red');
            this.renderer.appendChild(span, this.renderer.createText(part));
            this.renderer.appendChild(fragment, span);
          } else {
            this.renderer.appendChild(fragment, this.renderer.createText(part));
          }
        });

        node.parentNode?.replaceChild(fragment, node);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        // Skip already-highlighted spans
        if (el.tagName === 'SPAN' && el.style.color === 'red') {
          return;
        }
        Array.from(node.childNodes).forEach(walkTextNodes);
      }
    };

    walkTextNodes(working);

    // Apply only if content actually changed
    if (host.innerHTML !== working.innerHTML) {
      host.innerHTML = working.innerHTML;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.storeOriginalHTML();
      this.initialized = true;
      this.highlight();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && 'searchText' in changes) {
      this.highlight();
    }
  }
}
